import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMsal } from "@azure/msal-react";
import { callApi } from "../api/api";
import { clearAuthCaches } from "../utils/authCache";
import { isStrictTeamMember } from "../utils/profileDisplayRole";

const AccessContext = createContext(null);

export function AccessProvider({ children }) {
  const { instance, accounts } = useMsal();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [features, setFeatures] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hierarchyRoleCode, setHierarchyRoleCode] = useState(null);
  const [departmentName, setDepartmentName] = useState(null);
  const inFlightRef = useRef(null);
  const loadedEmailRef = useRef(null);
  const accountsRef = useRef(accounts);

  accountsRef.current = accounts;

  const account = instance.getActiveAccount() || accounts?.[0] || null;
  const accountEmail = account?.username || null;

  const resetAccess = useCallback(() => {
    setRoles([]);
    setPermissions([]);
    setFeatures([]);
    setIsAdmin(false);
    setHierarchyRoleCode(null);
    setDepartmentName(null);
    setError(null);
    loadedEmailRef.current = null;
  }, []);

  const loadAccess = useCallback(
    async ({ force = false } = {}) => {
      if (!accountEmail) {
        resetAccess();
        setLoading(false);
        return null;
      }

      if (
        !force &&
        loadedEmailRef.current === accountEmail &&
        inFlightRef.current == null
      ) {
        setLoading(false);
        return null;
      }

      if (inFlightRef.current) {
        return inFlightRef.current;
      }

      // Soft refresh keeps the current page mounted (toasts/forms stay alive).
      // Only the first bootstrap blocks the UI with loading=true.
      const isInitialLoad = loadedEmailRef.current == null;

      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      inFlightRef.current = (async () => {
        try {
          const res = await callApi(
            instance,
            accountsRef.current,
            "GET",
            `/api/user/me?_=${Date.now()}`,
          );

          const data = res?.data?.data || {};

          setRoles(data.roles || []);
          setPermissions(data.permissions || []);
          setFeatures(data.portalFeatures || []);
          setIsAdmin(Boolean(data.is_admin));
          setHierarchyRoleCode(data.hierarchyRoleCode || null);
          setDepartmentName(data.departmentName || data.department_name || null);
          setError(null);
          loadedEmailRef.current = accountEmail;
        } catch (err) {
          console.error("Access bootstrap (/me) failed", err);
          // Keep prior access snapshot on background refresh failure
          if (isInitialLoad) {
            resetAccess();
            setError("load_failed");
          }
        } finally {
          if (isInitialLoad) {
            setLoading(false);
          }
          inFlightRef.current = null;
        }
      })();

      return inFlightRef.current;
    },
    [instance, accountEmail, resetAccess],
  );

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  useEffect(() => {
    const handleAccessChanged = () => {
      clearAuthCaches();
      loadAccess({ force: true });
    };

    // notifyAuthContextChanged fires both events; listen once to avoid double fetch
    window.addEventListener("rbac-changed", handleAccessChanged);

    return () => {
      window.removeEventListener("rbac-changed", handleAccessChanged);
    };
  }, [loadAccess]);

  const isTeamMember = useMemo(
    () => isStrictTeamMember({ isAdmin, hierarchyRoleCode, roles }),
    [isAdmin, hierarchyRoleCode, roles],
  );

  const hasAccess = useCallback(
    (resource, access) => {
      if (!permissions?.length) return false;
      if (isAdmin) return true;

      // Team members do not have access to manual assignment or tracking analytics
      if (
        isTeamMember &&
        (resource === "MANUAL_ASSIGNMENT" || resource === "ANALYTICS")
      ) {
        return false;
      }

      // Request Leave (PTO) is accessible to team members and portal users
      if (resource === "REQUEST_LEAVE") {
        return true;
      }

      const hubResources = ["DOC_INDEX_HUB", "REQUEST_POOL", "MANUAL_ASSIGNMENT", "REQUEST_LEAVE"];
      const hasHubAccess = permissions.some(
        (p) =>
          (p.resource_code === "DOC_INDEX_HUB" || p.resource_code === "REQUEST_POOL") &&
          (p.access_level_code === access || p.access_level_code === "WRITE" || p.access_level_code === "READ"),
      );
      if (hasHubAccess && hubResources.includes(resource)) {
        return true;
      }

      return permissions.some(
        (permission) =>
          permission.resource_code === resource &&
          (permission.access_level_code === access ||
            permission.access_level_code === "WRITE"),
      );
    },
    [permissions, isAdmin, isTeamMember],
  );

  const value = useMemo(
    () => ({
      roles,
      permissions,
      features,
      isAdmin,
      isTeamMember,
      hierarchyRoleCode,
      departmentName,
      loading,
      error,
      hasAccess,
      reload: () => loadAccess({ force: true }),
    }),
    [
      roles,
      permissions,
      features,
      isAdmin,
      isTeamMember,
      hierarchyRoleCode,
      departmentName,
      loading,
      error,
      hasAccess,
      loadAccess,
    ],
  );

  return (
    <AccessContext.Provider value={value}>{children}</AccessContext.Provider>
  );
}

export function useAccess() {
  const context = useContext(AccessContext);

  if (!context) {
    throw new Error("useAccess must be used within AccessProvider");
  }

  return context;
}
